import React, { useContext, useEffect, useState } from 'react'
import MicroModal from 'micromodal'
import { useParams } from 'react-router-dom' 
import { LicensesStoreTableData, LicensesStoreTableType, MembersAssignedToLicense } from './data/LicensesStoreTableData'
import useLicenseTable from '../Licenses/context/LicenseTableContext'
import { TeamContext } from '../MenuTabs/MenuTabs'
import TanstackContent from '../Table/Tanstack/TanstackContent'
import { getExportHeaders } from '../ReportingGroup/types/tableHeaders'
import TableCheckbox from '../../../common/checkbox/TableCheckbox'
import Button from '../../../common/button/Button'
import Modal from '../../../common/modal/Modal'
import CollapsableText from '../../../common/card/CollapsableText'
import Icons from '../../../common/icons/Icons'
import ComboBoxList from '../../../common/combo-box/ComboBoxList'

const LicensesStoreTable = ({ licensesData, newPrice, setDisableCheckout, productRenewed, membersRenewedSelect, membersRenewed, total, membersSelection }) => {

  // console.log('productRenewed : ', productRenewed)
  // console.log('licensesData : ', licensesData)
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([])
  const [ rowSelection, setRowSelection ] = useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => LicensesStoreTableData( { 'licenses': licensesData.products } ) )
  const { setUseHeader, refetchLicenses } = useLicenseTable()
  const [ membersSelected, setMembersSelected ] = useState(membersRenewedSelect ?? [])
  const [ addToCartLoading, setAddToCartLoading ] = useState(false)

  const params = useParams()
  const teamId = params.team_id ?? 0
  const [productsQuantity, setProductsQuantity] = useState([])
  const [productsSelected, setProductsSelected] = useState([])

  useEffect(() => {
    let currentProductsSelected = [];
    (Object.keys(rowSelection)).map(row => {
      let license = data[row]
      currentProductsSelected.push(license.id)
    });
    setProductsSelected([...currentProductsSelected])
  }, [rowSelection])

  useEffect(() => {
    let currentPrice = 0
    productsSelected.forEach((productId) => {
      let quantity = productsQuantity[productId]
      if ( typeof quantity === 'undefined' && productRenewed.membersAssigned[productId] ) quantity = productRenewed.membersAssigned[productId]
      if ( typeof quantity === 'undefined' ) quantity = 1
      let currentProduct = (data).find((element) => element.id === productId )
      currentPrice += currentProduct.price * quantity
    })
    newPrice(currentPrice)
    const disableCheckoutButton = productsSelected.length === 0 ? true : false
    setDisableCheckout(disableCheckoutButton)
  }, [productsQuantity, productsSelected])

  useEffect(() => {
    MicroModal.init()
    setUseHeader(['all'])
    setRowSelection(productRenewed.rowsSelected)
  }, [])

  const columns = React.useMemo<ColumnDef<LicensesStoreTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => {
          let checkCurrentCheckbox = row.getIsSelected()
          return ( <div className="px-1">
            <TableCheckbox
              {...{
                checked: checkCurrentCheckbox,
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div> )
        },
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'name',
            accessorKey: 'name',
            prettyName: 'Name',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Name</span>,
          },
          {
            id: 'price',
            accessorKey: 'price',
            prettyName: 'Price',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Price</span>,
          },
          {
            id: 'id',
            accessorKey: 'id',
            prettyName: 'Product ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Product ID</span>,
          },
          {
            id: 'quantity',
            accessorKey: 'quantity',
            enableSorting: false,
            prettyName: 'Quantity',
            cell: (info) => {
              let productQuantityIndex = info.row.original.id
              let productQuantityValue = productsQuantity[productQuantityIndex]
              if ( typeof productQuantityValue === 'undefined' && productRenewed.membersAssigned[productQuantityIndex] ) productQuantityValue = productRenewed.membersAssigned[productQuantityIndex]           
              return (
                <input
                  type='number'
                  value={ productQuantityValue ?? 1}
                  min='1'
                  onChange={(e) => {
                    let currentProductQuantity = productsQuantity
                    currentProductQuantity[productQuantityIndex] = e.target.value
                    setProductsQuantity([...currentProductQuantity])
                  }}
                />
              )
            },
            footer: props => props.column.id,
            header: () => <span>Quantity</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={info} />
            ),
            footer: props => props.column.id,
            header: () => <span>Actions</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const MembersColumn = React.useMemo<ColumnDef<MembersAssignedToLicense>[]>(
    () => [
      {
        id: 'columns',
        columns: [
          {
            id: 'username',
            accessorKey: 'username',
            prettyName: 'Name',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Name</span>,
          },
          {
            id: 'email',
            accessorKey: 'email',
            prettyName: 'Email',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Email</span>,
          },
          {
            id: 'expires',
            accessorKey: 'expires',
            prettyName: 'Expires',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Expires</span>,
          },
        ]
      }
    ]
  )

  const Actions = ({ info }) => {
    return (
      (info.row).getCanExpand() ? (
        <Button
          className="msync-button--is-style-outline"
          type="button"
          name={<span className="msync-product-details-button-content">Details {(info.row).getIsExpanded() ? <Icons type='arrow-up' /> : <Icons type='arrow-down' />}</span>}
          {...{
            onClick: (info.row).getToggleExpandedHandler()
          }}
          aria-label="Close this dialog window"
        />
      ) : '' 
    )
  }

  const renderSubComponent = (row) => {
    let totalOrgLicenses = licensesData.products[row.index].orgLicensesTotal
    let orgLicensesAssigned = licensesData.products[row.index].orgLicensesAssigned

    const warningIcon = {
      name        : 'tooltip-transfered-members',
      type        : 'tooltip',
      layout      : 'text', 
      label       : <Icons type='warning' />,
      placement   : 'top', 
      theme       : 'light',
      content     : 'Member transfered from the product initially purchased.'
    };

    (orgLicensesAssigned.membersAssigned).forEach((member, index) => {
      if ( member.transfered && typeof member.username === 'string' ) {
        member.username = <span class="licenses-store-member-assigned-username-container"> {member.username} {tangibleFields.render(warningIcon, 'element')} </span> 
        orgLicensesAssigned.membersAssigned[index] = member
      }
    })

    return (
      <div className="msync-product-details-container">
        <div className="msync-product-details-meta">
          <div className="msync-product-details-left-border">
            <span>Assigned: {orgLicensesAssigned.assigned}</span>
          </div>
          <div className="msync-product-details-left-border">
            <span>Owned: {totalOrgLicenses}</span>
          </div>
        </div>
        <div>
          <CollapsableText collapsed={false} title='Assigned Licenses'>
            <>
              <TanstackContent
                data={ orgLicensesAssigned.membersAssigned }
                columns={ MembersColumn }
                rowSelection={ rowSelection }
                setRowSelection={ setRowSelection }
                sorting={ sorting }
                setSorting={ setSorting }
                globalFilter={ globalFilter }
                setGlobalFilter={ setGlobalFilter }
                exportHeaders={ exportHeaders }
                from='members'
                context='members'
                hideFilters={ true }
                columnFilters={ columnFilters }
                setColumnFilters={ setColumnFilters }
              >
              </TanstackContent>
            </>
          </CollapsableText>
        </div>
      </div>
    )
  }

  const renewLicense = async () => {
    const currentRestUrl = window.group_dashboard.base
    const nonce = window.group_dashboard.nonce

    const url = currentRestUrl + 'membersync/licensing/v1/org/' + teamId + '/renew/license'

    const licenseInfos = []
    let currentMembersSelected = membersSelected
    // console.log('licensesData : ', licensesData)
    productsSelected.map(productId => {
      let currentProductQuantity = productsQuantity[productId]
      if ( typeof currentProductQuantity === 'undefined' && productRenewed.membersAssigned[productId] ) currentProductQuantity = productRenewed.membersAssigned[productId]
      if ( typeof currentProductQuantity === 'undefined' ) currentProductQuantity = 1
      let currentLicenseInfos = {
        total: currentProductQuantity,
        productID: productId
      }
      licenseInfos.push(currentLicenseInfos)

      const currentProductID = productRenewed.product[0]
      const currentProduct = (licensesData.products).find(element => element.id === productId)
      const currentProductMembers = currentProduct.orgLicensesAssigned.membersAssigned

      if ( typeof productRenewed.recommendation !== 'undefined' && typeof productRenewed.recommendation[productId] !== 'undefined' && productRenewed.recommendation[productId] === currentProductID ) {
        currentMembersSelected.forEach((currentMemberSelected, memberIndex) => {
          const currentProductMember = currentProductMembers.find(element => element.actor_id === currentMemberSelected.value)
          // console.log('currentProductMember : ', currentProductMember)
          if ( typeof currentProductMember === 'undefined' ) {
            currentMemberSelected.transfered = false
          } else {
            currentMemberSelected.transfered = currentProductMember.transfered
          }
          currentMembersSelected[memberIndex] = currentMemberSelected
        });
      }
    })
    
    // console.log('currentMembersSelected : ', currentMembersSelected)

    try {
      const res = await fetch(url, {
          method: 'POST',
          body: JSON.stringify({
            'license_infos': licenseInfos,
            'members': currentMembersSelected,
            'product_renewed' : productRenewed
          }),
          headers:{
            'X-WP-Nonce': nonce,
            'Content-type': 'application/json'
          }
      })
      const resultJSON = await res.json()
      // console.log('resultJSON : ', resultJSON)
      // if ( resultJSON.response.checkout_url ) window.location.href = resultJSON.response.checkout_url 
      return resultJSON.response

    } catch (error) {
      console.error( error )
      return false
    }
  }

  const handleCloseModal = (modalName) => {
    MicroModal.close(modalName)
  }

  const renderMembers = () => {
    return (
      Array.isArray(membersSelected) && membersSelected.length !== 0 && membersSelected.map((member) => {
        return (
          <div className="msync-form__row msync-license-assignment-contents-border-bottom">
            <div className="msync-license-assignment-user-contents">
              { member?.label }
            </div>
          </div>
        )
      })
    )
  }

  const modalId = "msync-confirm-assign-licenses-after-purchase-" + licensesData.renewal.product[0]
  const currentFieldName = "msync-combo-box-assign-renewal-licenses-members-" + licensesData.renewal.product[0]

  tangibleFields.event('valueChange', (field, event) => {
    if( field.name === currentFieldName ) {
      if ( field.value ) setMembersSelected( field.value )
    }
  })

  const renderProductsCantAssigned = () => {

    const productCantAssigned = []

    if ( !Array.isArray(membersSelected) || productsSelected.length === 0 ) return

    productsSelected.map(productId => {
      let currentProduct = data.find((element) => element.id === productId)
      let currentProductQuantity = productsQuantity[productId]
      if ( typeof currentProductQuantity === 'undefined' && productRenewed.membersAssigned[productId] ) currentProductQuantity = productRenewed.membersAssigned[productId]
      if ( typeof currentProductQuantity === 'undefined' ) currentProductQuantity = 1
      if ( currentProductQuantity < membersSelected.length ) productCantAssigned.push(currentProduct.name)
    })

    if ( productCantAssigned.length === 0 ) return

    return (
      <div className='msync-insufficient-licenses'>
        <div className='msync-insufficient-licenses-title'>
          Insufficient licenses
        </div>
        <div>
          { productCantAssigned.map((productName, index) => {
            // console.log('productName - index : ', productName, index)
            if ( index === 0 ) {
              return productName
            } else {
              return ',' + productName
            }
          }) + ' ' }
          will not be assigned to the selected members but will still be bought for your organization.
        </div>
      </div> 
    )
  }

  const exportHeaders = getExportHeaders('team-licenses')
  const { teamData } = useContext(TeamContext)

  return (
    <>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Licenses'
        context='Licenses'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
        renderSubComponent={renderSubComponent}
      >
      </TanstackContent>
      <Modal
        modalId={modalId} 
        buttonTitle="" 
        modalHeading='Confirm assignment'
        modalClassNames={ 'msync-assign-licenses-custom-modal msync-assign-licenses-custom-modal-border'}
      >
        <main className="msync-modal__body">
          <div className="msync-modal-content-section">
              <div key={''} className="msync-form">
                <div className="msync-display-flex">
                  <div className="msync-form__licenses-users">
                    {
                      tangibleFields.render({
                        type: 'combo-box',
                        name: currentFieldName,
                        label: '',
                        multiple: true,
                        // choices: data.selects.members
                        isAsync: true,
                        ajaxAction: 'get_members_select_assigned_licenses',
                        asyncArgs: {
                          org_id: teamId
                        },
                        value: membersSelected
                      })
                    }
                    {/* <ComboBoxList
                      fieldName={currentFieldName}
                      choices={membersSelection}
                      selectionsEdited={(value) => {
                        // setMembersSelected(value)
                      }}
                      defaultValues={membersSelected}
                    /> */}
                    {
                      renderMembers()
                    }
                  </div>
                  <div className="msync-licenses-container">
                    <div className="msync-form__licenses-licenses">
                      {
                        productsSelected.map(productId => {
                          let currentProduct = data.find((element) => element.id === productId)
                          let currentProductQuantity = productsQuantity[productId]
                          if ( typeof currentProductQuantity === 'undefined' && productRenewed.membersAssigned[productId] ) currentProductQuantity = productRenewed.membersAssigned[productId]
                          if ( typeof currentProductQuantity === 'undefined' ) currentProductQuantity = 1
                          return (
                            <div className="msync-license-assignment-user-contents msync-license-assignment-contents-bold">
                              { currentProduct.name } - { currentProductQuantity }
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                </div>
              </div>
            
            {
              <div className="msync-form__row">
                {
                  renderProductsCantAssigned()
                }
              </div>
            }
          </div>
          <footer className="modal__footer">
            <Button
              className="msync-button--is-style-outline msync-button--is-theme-destructive"
              type="button"
              name="Cancel"
              onClick={ () => handleCloseModal(modalId) }
              aria-label="Close this dialog window"
              isDisabled={ addToCartLoading }
            />
            <Button
              className="msync-primary-btn"
              onClick={ async () => {
                setAddToCartLoading( true )
                const result = await renewLicense()
                setAddToCartLoading( false )
                window.location.replace( result.checkout_url )
              }}
              name={ addToCartLoading ? "Loading..." : "Proceed to checkout"}
              isDisabled={ addToCartLoading }
            />
          </footer>
        </main>
      </Modal>
    </>
  )
}

export default LicensesStoreTable
